import React, { useRef, useState, useEffect, useContext } from 'react';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { IconEraser } from '@tabler/icons-react';
import { KTDom, KTModal } from '@/metronic/core';
import ListAddElementModal from '@/components/Custom/Modal/ListAddElementModal';
import { Rectangle, Polygon } from '@/entities/canvas';
import {
  getColorByValue,
  getScaledCoordinates,
  convertSegToCanvasData,
} from '@/utils/ia.utils';
import { MODE_DRAW } from '@/utils/constant.utils';
import { Button } from '@/components/Button';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { TFunction } from 'i18next';

export const CANVAS_WIDTH = 700;
export const CANVAS_HEIGHT = 500;

type CanvasProps = {
  t?: TFunction<undefined>;
  imageSrc: string;
  color?: string;
  listAnnotation: TResultImageCoordinatesSchema[];
  showElement: boolean;
  equipmentClassId?: number;
  confirmAddElement?: () => void;
  mode: string;
};

type ElementDraw = {
  rectangle: Rectangle;
  color: string;
  elNumber?: string;
  index?: number;
};

const Canvas = ({
  t,
  imageSrc,
  listAnnotation,
  showElement,
  equipmentClassId,
  confirmAddElement,
  mode,
}: CanvasProps) => {
  const {
    isDeleted = false,
    setIsdDeleted,
    currentElement: currentHighlightEl,
    setCurrentElCanvas,
    setCurrentElement,
  } = useContext(EngineCorrectionContext);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [newColorToDraw] = useState<string>('');
  const [newRectangle, setNewRectangle] = useState<Rectangle>();
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>();
  const elementDrawRef = useRef<ElementDraw[]>([]);
  const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null);
  const [currentPolygon, setCurrentPolygon] = useState<Polygon>([]);
  const [newPolygon, setNewPolygon] = useState<Polygon>([]);
  const [erase, setErase] = useState<boolean>(false);
  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { allDamagesSelectOptions } = useGetModerationDamages();

  useEffect(() => {
    if (canvasRef && canvasRef.current) elementDrawRef.current = [];
  }, [showElement]);

  // Load the image when the component mounts or when the src changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      setWidth(image.width);
      setHeight(image.height);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      setImageLoaded(true);
      drawYOLODataRectangles(); // Draw any saved rectangles after image load
      drawYOLODataPolygones();
    };
  }, [imageSrc, currentHighlightEl, listAnnotation, width, height, erase]);

  useEffect(() => {
    drawCurrentPolygone();
  }, [currentPolygon, listAnnotation]);

  const checkHighLight = (polygonElNumber, index) => {
    return (
      polygonElNumber === currentHighlightEl['elNumber'] &&
      index === currentHighlightEl['index']
    );
  };

  const drawYOLODataPolygones = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (listAnnotation.length > 0) {
      listAnnotation?.forEach((polygon, index) => {
        ctx.beginPath();
        if (polygon?.point?.length > 0) {
          ctx.moveTo(
            convertSegToCanvasData(polygon?.point[0]?.x, canvas.width),
            convertSegToCanvasData(polygon?.point[0]?.y, canvas.height)
          );
        }
        polygon.point?.forEach((p) =>
          ctx.lineTo(
            convertSegToCanvasData(p.x, canvas.width),
            convertSegToCanvasData(p.y, canvas.height)
          )
        );
        ctx.closePath();
        ctx.strokeStyle = checkHighLight(polygon.elNumber, index)
          ? 'red'
          : 'blue';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = checkHighLight(polygon.elNumber, index)
          ? 'rgba(132, 20, 37, 0.3)'
          : 'rgba(0, 0, 255, 0.3)';
        ctx.fill();

        // Draw small circles for each intersection (vertex)
        // polygon.point?.forEach((p) => {
        //   ctx.beginPath();
        //   ctx.arc(convertSegToCanvasData(p.x, canvas.width), convertSegToCanvasData(p.y, canvas.height), 5, 0, 2 * Math.PI); // Mini circle at each vertex
        //   ctx.fillStyle = 'red';
        //   ctx.fill();
        //   ctx.closePath();
        // });
      });
    }
  };

  const drawCurrentPolygone = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (currentPolygon.length > 0) {
      ctx.beginPath();
      ctx.moveTo(currentPolygon[0].x, currentPolygon[0].y);
      currentPolygon.forEach((point) => ctx.lineTo(point.x, point.y));
      ctx.strokeStyle = 'red';
      ctx.lineWidth = 6;
      ctx.stroke();

      // Draw small circles for each intersection (vertex)
      currentPolygon.forEach((point) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 10, 0, 2 * Math.PI); // Mini circle at each vertex
        ctx.fillStyle = 'red';
        ctx.fill();
        ctx.closePath();
      });
    }
  };

  const drawYOLODataRectangles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (isDeleted) elementDrawRef.current = [];
    listAnnotation.map((list, index) => {
      const { elNumber, xCenter, yCenter, width, height } = list;
      const startX = (xCenter - width / 2) * canvas.width;
      const startY = (yCenter - height / 2) * canvas.height;
      const rectWidth = width * canvas.width;
      const rectHeight = height * canvas.height;

      // Add rectangle to elementDraw
      elementDrawRef.current = [
        ...elementDrawRef.current,
        {
          color: getColorByValue(
            elNumber?.toString(),
            showElement,
            equipmentsSelectOptions,
            allDamagesSelectOptions
          ),
          rectangle: {
            startX,
            startY,
            endX: startX + rectWidth,
            endY: startY + rectHeight,
          },
          elNumber,
          index,
        },
      ];
    });

    // Redraw all rectangles
    drawAllRectangles();
    setIsdDeleted(false);
  };

  useEffect(() => {
    drawAllRectangles();
  }, [elementDrawRef]);

  const drawAllRectangles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    elementDrawRef.current.forEach((el) => {
      const width = el.rectangle.endX - el.rectangle.startX;
      const height = el.rectangle.endY - el.rectangle.startY;
      ctx.strokeStyle = el.color;
      ctx.lineWidth =
        el.elNumber === currentHighlightEl['elNumber'] &&
        el.index === currentHighlightEl['index']
          ? 10
          : 4;
      ctx.strokeRect(el.rectangle.startX, el.rectangle.startY, width, height);
    });
  };

  const handleDoubleClick = () => {
    if (mode === MODE_DRAW.RECTANGLE) return;
    // if (currentPolygon.length > 2) {
    //   setPolygons((prev) => [...prev, currentPolygon]);
    // }
    setNewPolygon(currentPolygon);
    setCurrentPolygon([]);
    showModalelement();
  };

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (mode === MODE_DRAW.RECTANGLE) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getScaledCoordinates(e.clientX, e.clientY, canvasRef);
    setCurrentPolygon((prev) => [...prev, { x, y }]);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!imageLoaded || mode === MODE_DRAW.POLYGONE) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x, y } = getScaledCoordinates(e.clientX, e.clientY, canvasRef);
    setStartPoint({ x, y });

    setIsDrawing(true);
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || mode === MODE_DRAW.POLYGONE) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const { x: endXRect, y: endYRect } = getScaledCoordinates(
      e.clientX,
      e.clientY,
      canvasRef
    );

    const newRectangleIndex = elementDrawRef.current.length;

    // Ajout du nouveau rectangle
    elementDrawRef.current = [
      ...elementDrawRef.current,
      {
        color: newColorToDraw,
        rectangle: {
          startX: startPoint.x,
          startY: startPoint.y,
          endX: endXRect,
          endY: endYRect,
        },
      },
    ];
    setNewRectangle({
      startX: startPoint.x,
      startY: startPoint.y,
      endX: endXRect,
      endY: endYRect,
    });

    setLastAddedIndex(newRectangleIndex);

    setIsDrawing(false);
    showModalelement();
  };

  const handleCancel = () => {
    elementDrawRef.current.splice(lastAddedIndex, 1);
    setLastAddedIndex(null);
    setNewRectangle(undefined);
    setCurrentPolygon([]);
    setErase((prev) => !prev);
  };

  const showModalelement = () => {
    const modalEl = KTDom.getElement('#list-element-modal');
    const modal = KTModal.getInstance(modalEl);
    modal?.show();
  };

  const validButton = () => {
    setErase((prev) => !prev);
    if (confirmAddElement) confirmAddElement();
  };

  const eraseCurrent = () => {
    setErase((prev) => !prev);
    setCurrentPolygon([]);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!imageLoaded || mode === MODE_DRAW.POLYGONE) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x: currentX, y: currentY } = getScaledCoordinates(
      e.clientX,
      e.clientY,
      canvasRef
    );
    // Effacez le canvas et redessinez
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      drawAllRectangles(); // Redessine les rectangles sauvegardés

      // let isHovering = false;
      let hoveredEl: ElementDraw | null = null;

      // Check if mouse is over any rectangle
      if (!isDrawing) {
        elementDrawRef.current.forEach((el) => {
          const { rectangle } = el;
          const { startX, startY, endX, endY } = rectangle;

          if (
            currentX >= startX &&
            currentX <= endX &&
            currentY >= startY &&
            currentY <= endY
          ) {
            // isHovering = true;
            hoveredEl = el;

            // Highlight the hovered rectangle
            const width = endX - startX;
            const height = endY - startY;
            ctx.strokeStyle = el.color; // Highlight color
            ctx.lineWidth = 10;
            ctx.strokeRect(startX, startY, width, height);
            setCurrentElCanvas({
              index: hoveredEl.index,
              elNumber: hoveredEl.elNumber,
            });

            setCurrentElement({
              index: hoveredEl.index,
              elNumber: hoveredEl.elNumber,
            });
          }
        });
      }

      // Dessine le rectangle en cours
      if (isDrawing) {
        const width = currentX - startPoint.x;
        const height = currentY - startPoint.y;
        ctx.strokeStyle = newColorToDraw;
        ctx.lineWidth = 10;
        ctx.strokeRect(startPoint.x, startPoint.y, width, height);
      }
    };
  };

  return (
    <div>
      <div className="relative">
        <canvas
          className="w-full h-auto"
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onClick={handleCanvasClick}
          onDoubleClick={handleDoubleClick}
        />
        <Button
          buttonClassName={`btn btn-danger absolute bottom-2 right-2 px-2 cursor-pointer ${
            !showElement && currentPolygon.length > 0 ? '' : 'hidden'
          }`}
          text={t('common:erase')}
          onClick={eraseCurrent}
          leftIcon={<IconEraser stroke={2} />}
        />
      </div>
      <ListAddElementModal
        t={t}
        id="list-element-modal"
        equipmentClassId={equipmentClassId}
        isElement={showElement}
        newRectangle={newRectangle}
        newPolygon={newPolygon}
        onClickValidButton={validButton}
        canvasRef={canvasRef}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default Canvas;
